<template>
  <div class="address-container">
    <!-- 筛选栏 -->
    <div class="filter-bar">
      <button class="add-btn" @click="showAddDialog">
        <i class="fas fa-plus"></i>
        {{ $t('_bddz._tjdz') }}
      </button>

      <div class="search-box">
        <i class="fas fa-search"></i>
        <input
          v-model="filters.search"
          type="text"
          :placeholder="$t('_bddz._ssdz')"
          class="search-input"
          @input="onSearch"
        />
        <button v-if="filters.search" class="clear-btn" @click="filters.search = ''">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- 地址列表 -->
    <div class="table-wrapper">
      <div class="table-header">
        <div class="header-cell currency">{{ $t('_bddz._bz') }}</div>
        <div class="header-cell address">{{ $t('_bddz._dz') }}</div>
        <div class="header-cell time">{{ $t('_bddz._sysj') }}</div>
        <div class="header-cell actions">{{ $t('_bddz._cz') }}</div>
      </div>

      <!-- 列表项 -->
      <div
        v-for="item in addresses"
        :key="item.id"
        class="table-row"
        :class="{ recent: isRecent(item.create_time) }"
      >
        <div class="row-cell currency">
          <div class="currency-info">
            <span class="currency-name">{{ item.name }}</span>
            <span class="currency-network">{{ item.network }}</span>
          </div>
        </div>

        <div class="row-cell address">
          <div class="address-display">
            <span class="address-text" :title="item.address">
              {{ formatAddress(item.address) }}
            </span>
            <div class="address-actions">
              <button class="action-btn" @click="copyAddress(item.address)" title="复制">
                <CopyOutlined />
              </button>
              <button class="action-btn" @click="showQRCode(item)" title="二维码">
                <QrcodeOutlined />
              </button>
            </div>
          </div>
        </div>

        <div class="row-cell time">
          <div class="time-info">
            <span class="time-date">{{ formatDate(item.create_time) }}</span>
            <span class="time-diff">{{ getTimeDiff(item.create_time) }}</span>
          </div>
        </div>

        <div class="row-cell actions">
          <button class="action-btn delete" @click="deleteAddress(item)" title="删除">
            <DeleteOutlined />
          </button>
          <button class="action-btn view" @click="showDetails(item)" title="详情">
            <EyeOutlined />
          </button>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="addresses.length === 0 && !loading" class="empty-state">
        <i class="fas fa-address-book"></i>
        <p>{{ $t('_bddz._zwjl') }}</p>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>{{ $t('_a._jzz') }}</p>
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination-bar" v-if="addresses.length > 0">
      <button class="page-btn" :disabled="page === 1" @click="onPage(page - 1)">
        <LeftOutlined />
      </button>
      <span class="page-info">{{ $t('_bddz._dny', { a: page, b: totalPages }) }}</span>
      <button class="page-btn" :disabled="page === totalPages" @click="onPage(page + 1)">
        <RightOutlined />
      </button>
    </div>

    <!-- ===== 添加地址弹窗 ===== -->
    <div v-if="showAddModal" class="modal-overlay" @click.self="closeModals">
      <div class="modal-content">
        <div class="modal-header">
          <span class="modal-title">{{ $t('_tjdz._tjxd') }}</span>
          <button class="modal-close" @click="closeModals">
            <CloseCircleOutlined />
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>{{ $t('_tjdz._xzbz') }}</label>
            <select v-model="pIndex" class="form-select" @change="onSelectChange">
              <option v-for="(vo, i) in protocol" :key="i" :value="i">
                {{ vo.name }} {{ vo.name == vo.network ? '' : '(' + vo.network + ')' }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>{{ $t('_tjdz._qbdz') }}</label>
            <input
              v-model="newAddress.address"
              type="text"
              :placeholder="$t('_tjdz._srdz')"
              class="form-input"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeModals">{{ $t('_tjdz._qx') }}</button>
          <button class="btn-confirm" @click="addNewAddress">{{ $t('_tjdz._bc') }}</button>
        </div>
      </div>
    </div>

    <!-- ===== 二维码弹窗 ===== -->
    <div v-if="showQRModal" class="modal-overlay" @click.self="closeModals">
      <div class="modal-content qr-modal">
        <div class="modal-header">
          <span class="modal-title">{{ $t('_bddz._dzewm') }}</span>
          <button class="modal-close" @click="closeModals">
            <CloseCircleOutlined />
          </button>
        </div>
        <div class="modal-body qr-body">
          <canvas ref="qrCanvas" class="qr-canvas"></canvas>
          <div class="qr-info">
            <div class="qr-currency">{{ selectedAddress?.name }}</div>
            <div class="qr-address">{{ selectedAddress?.address }}</div>
            <button class="copy-qr-btn" @click="copyAddress(selectedAddress?.address)">
              <i class="fas fa-copy"></i>
              {{ $t('_bddz._fzdz') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== 详情弹窗 ===== -->
    <div v-if="showDetailModal" class="modal-overlay" @click.self="closeModals">
      <div class="modal-content">
        <div class="modal-header">
          <span class="modal-title">{{ $t('_bddz._dzxq') }}</span>
          <button class="modal-close" @click="closeModals">
            <CloseCircleOutlined />
          </button>
        </div>
        <div class="modal-body">
          <div class="detail-item">
            <span class="detail-label">{{ $t('_bddz._bz') }}</span>
            <span class="detail-value">{{ selectedAddress?.name }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ $t('_bddz._wl') }}</span>
            <span class="detail-value">{{ selectedAddress?.network }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ $t('_bddz._dz') }}</span>
            <span class="detail-value address-full">{{ selectedAddress?.address }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ $t('_bddz._tjsj') }}</span>
            <span class="detail-value">{{ formatFullTime(selectedAddress?.create_time) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  CopyOutlined,
  QrcodeOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  RightOutlined,
  LeftOutlined
} from '@ant-design/icons-vue'
import QRCode from 'qrcode'

export default {
  name: 'AddressManagement',
  components: {
    CopyOutlined,
    QrcodeOutlined,
    DeleteOutlined,
    EyeOutlined,
    RightOutlined,
    LeftOutlined,
    CloseCircleOutlined
  },
  data() {
    return {
      addresses: [],
      protocol: [],
      newAddress: {
        c_id: 0,
        address: '',
        note: ''
      },
      selectedAddress: null,
      pIndex: 0,
      page: 1,
      pageSize: 10,
      total: 0,
      loading: false,
      showAddModal: false,
      showQRModal: false,
      showDetailModal: false,
      filters: {
        search: ''
      },
      searchTimer: null
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    }
  },
  created() {
    this.getList()
    this.getProtocols()
  },
  methods: {
    // ===== 数据获取 =====
    getList() {
      this.loading = true
      const hide = this.$message.loading({ content: this.$t('_a._jzz'), key: 'msg' })

      this.$api.userWallet
        .getWalletAddressList({
          page: this.page,
          size: this.pageSize,
          search: this.filters.search
        })
        .then((res) => {
          hide()
          this.addresses = res.data?.data || []
          this.total = res.data?.total || 0
        })
        .catch(() => {
          hide()
          this.addresses = []
          this.total = 0
        })
        .finally(() => {
          this.loading = false
        })
    },

    getProtocols() {
      this.$api.chain.getCurrencyItemList().then((res) => {
        this.protocol = res.data || []
        if (this.protocol.length > 0) {
          this.newAddress.c_id = this.protocol[0].c_id
        }
      })
    },

    // ===== 分页 =====
    onPage(page) {
      if (page < 1 || page > this.totalPages) return
      this.page = page
      this.getList()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    // ===== 搜索 =====
    onSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.page = 1
        this.getList()
      }, 300)
    },

    // ===== 格式化 =====
    formatAddress(address) {
      if (!address) return ''
      if (address.length <= 16) return address
      return address.substring(0, 8) + '...' + address.substring(address.length - 8)
    },

    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('zh-CN')
    },

    formatFullTime(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('zh-CN')
    },

    getTimeDiff(date) {
      if (!date) return ''
      const now = new Date()
      const diff = now - new Date(date)
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor(diff / (1000 * 60))

      if (days > 0) return this.$t('_bddz._ntq', { a: days })
      if (hours > 0) return this.$t('_bddz._nxsq', { a: hours })
      if (minutes > 0) return this.$t('_bddz._nfzq', { a: minutes })
      return this.$t('_bddz._ngg')
    },

    isRecent(date) {
      if (!date) return false
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      return new Date(date) > sevenDaysAgo
    },

    // ===== 复制 =====
    copyAddress(address) {
      if (!address) return
      navigator.clipboard.writeText(address).then(() => {
        this.$message.success(this.$t('_bddz._dzyfz'))
      }).catch(() => {
        // 降级方案
        const input = document.createElement('input')
        input.value = address
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        this.$message.success(this.$t('_bddz._dzyfz'))
      })
    },

    // ===== 添加地址 =====
    showAddDialog() {
      this.showAddModal = true
    },

    onSelectChange(e) {
      const idx = parseInt(e.target.value)
      this.pIndex = idx
      if (this.protocol[idx]) {
        this.newAddress.c_id = this.protocol[idx].c_id
      }
    },

    addNewAddress() {
      if (!this.newAddress.address || !this.newAddress.address.trim()) {
        this.$message.error(this.$t('_tjdz._srdz'))
        return
      }

      const hide = this.$message.loading({ content: this.$t('_a._bcz'), key: 'msg' })
      this.$api.userWallet
        .saveAddress(this.newAddress)
        .then((res) => {
          hide()
          if (res.code === 1) {
            this.$message.warning({ content: res.msg, key: 'msg' })
            return
          }
          this.$message.success({ content: res.msg || '添加成功', key: 'msg' })
          this.closeModals()
          this.getList()
        })
        .catch(() => {
          hide()
        })
    },

    // ===== 删除地址 =====
    deleteAddress(item) {
      this.$confirm({
        title: this.$t('_bddz._scqr'),
        content: this.$t('_bddz._scqrdz'),
        okText: this.$t('_bddz._qr'),
        cancelText: this.$t('_bddz._qx'),
        onOk: () => {
          this.$api.userWallet
            .delWalletAddress({ id: item.id })
            .then(() => {
              this.$message.success(this.$t('_bddz._scdzcg'))
              this.getList()
            })
            .catch(() => {})
        }
      })
    },

    // ===== 二维码 =====
    showQRCode(address) {
      this.selectedAddress = address
      this.showQRModal = true
      this.$nextTick(() => {
        this.makeQRCode(address.address)
      })
    },

    async makeQRCode(address) {
      try {
        await QRCode.toCanvas(this.$refs.qrCanvas, address, {
          width: 200,
          height: 200,
          margin: 1,
          color: {
            dark: '#e8edf2',
            light: '#0f1419'
          }
        })
      } catch (error) {
        console.error('二维码生成失败:', error)
      }
    },

    // ===== 详情 =====
    showDetails(address) {
      this.selectedAddress = address
      this.showDetailModal = true
    },

    // ===== 关闭弹窗 =====
    closeModals() {
      this.showAddModal = false
      this.showQRModal = false
      this.showDetailModal = false
      this.selectedAddress = null
    }
  }
}
</script>

<style scoped>
/* ============================================================
   地址管理 - 暗色主题
   ============================================================ */

.address-container {
  background: transparent;
  color: #e8edf2;
}

/* ============================================================
   筛选栏
   ============================================================ */
.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;
  flex-wrap: wrap;
}

.add-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: linear-gradient(135deg, #e50914, #b20710);
  color: #fff;
  border: none;
  border-radius: 50px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(229, 9, 20, 0.3);
}

.add-btn i {
  font-size: 14px;
}

.search-box {
  display: flex;
  align-items: center;
  background: #181e24;
  border-radius: 24px;
  padding: 0 16px;
  border: 1px solid #1e262e;
  transition: border-color 0.3s, box-shadow 0.3s;
  flex: 1;
  max-width: 360px;
}

.search-box:focus-within {
  border-color: #e50914;
  box-shadow: 0 0 0 3px rgba(229, 9, 20, 0.1);
}

.search-box i {
  color: #5a6672;
  font-size: 14px;
  margin-right: 10px;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #e8edf2;
  font-size: 14px;
  padding: 9px 0;
}

.search-input::placeholder {
  color: #4a5662;
}

.clear-btn {
  background: transparent;
  border: none;
  color: #5a6672;
  cursor: pointer;
  padding: 0 4px;
  font-size: 14px;
  transition: color 0.2s;
}

.clear-btn:hover {
  color: #e8edf2;
}

/* ============================================================
   表格
   ============================================================ */
.table-wrapper {
  background: #0f1419;
  border-radius: 14px;
  border: 1px solid #1a1f26;
  overflow: hidden;
}

.table-header {
  display: flex;
  padding: 12px 20px;
  background: #0a0d0f;
  border-bottom: 1px solid #1a1f26;
}

.header-cell {
  color: #5a6672;
  font-size: 15px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0 6px;
}

.header-cell.currency { flex: 1.2; }
.header-cell.address { flex: 3; }
.header-cell.time { flex: 1.2; }
.header-cell.actions { flex: 1; text-align: center; }

/* 行 */
.table-row {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid #141a20;
  transition: background 0.2s;
}

.table-row:hover {
  background: rgba(255, 255, 255, 0.02);
}

.table-row:last-child {
  border-bottom: none;
}

.table-row.recent {
  border-left: 3px solid #e50914;
}

.row-cell {
  display: flex;
  align-items: center;
  padding: 0 6px;
  min-height: 44px;
}

.row-cell.currency { flex: 1.2; }
.row-cell.address { flex: 3; }
.row-cell.time { flex: 1.2; }
.row-cell.actions { flex: 1; justify-content: center; gap: 4px; }

/* ============================================================
   货币信息
   ============================================================ */
.currency-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.currency-name {
  color: #e8edf2;
  font-size: 14px;
  font-weight: 500;
}

.currency-network {
  color: #5a6672;
  font-size: 12px;
}

/* ============================================================
   地址
   ============================================================ */
.address-display {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.address-text {
  color: #8a949f;
  font-size: 14px;
  font-family: monospace;
  letter-spacing: 0.3px;
  word-break: break-all;
}

.address-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.address-actions .action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #5a6672;
  cursor: pointer;
  transition: all 0.2s;
}

.address-actions .action-btn:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #e8edf2;
}

/* ============================================================
   时间
   ============================================================ */
.time-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.time-date {
  color: #e8edf2;
  font-size: 13px;
}

.time-diff {
  color: #5a6672;
  font-size: 12px;
}

/* ============================================================
   操作按钮
   ============================================================ */
.row-cell.actions .action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #5a6672;
  cursor: pointer;
  transition: all 0.25s;
  font-size: 15px;
}

.row-cell.actions .action-btn:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #e8edf2;
}

.row-cell.actions .action-btn.delete:hover {
  background: rgba(229, 9, 20, 0.12);
  color: #e50914;
}

.row-cell.actions .action-btn.view:hover {
  background: rgba(38, 166, 154, 0.12);
  color: #26a69a;
}

/* ============================================================
   空状态
   ============================================================ */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #5a6672;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.4;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
}

/* ============================================================
   加载状态
   ============================================================ */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #5a6672;
}

.loading-state i {
  font-size: 32px;
  margin-bottom: 10px;
  color: #e50914;
}

.loading-state p {
  font-size: 14px;
  margin: 0;
}

/* ============================================================
   分页
   ============================================================ */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px 0 0;
}

.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #1a1f26;
  border-radius: 8px;
  background: #0f1419;
  color: #8a949f;
  cursor: pointer;
  transition: all 0.25s;
}

.page-btn:hover:not(:disabled) {
  background: #181e24;
  border-color: #2a3138;
  color: #e8edf2;
}

.page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.page-info {
  color: #8a949f;
  font-size: 14px;
}

/* ============================================================
   弹窗
   ============================================================ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.25s ease;
}

.modal-content {
  width: 440px;
  max-width: 92vw;
  background: #141a20;
  border-radius: 16px;
  border: 1px solid #1e262e;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6);
  animation: slideUp 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.modal-content.qr-modal {
  width: 360px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid #1a1f26;
}

.modal-title {
  color: #e8edf2;
  font-size: 18px;
  font-weight: 600;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #5a6672;
  cursor: pointer;
  font-size: 20px;
  transition: all 0.25s;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #e8edf2;
  transform: rotate(90deg);
}

.modal-body {
  padding: 20px 24px 24px;
}

/* ===== 表单 ===== */
.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  color: #8a949f;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
}

.form-select,
.form-input {
  width: 100%;
  padding: 10px 14px;
  background: #0a0d0f;
  border: 1px solid #1a1f26;
  border-radius: 10px;
  color: #e8edf2;
  font-size: 14px;
  transition: border-color 0.3s;
  outline: none;
}

.form-select:focus,
.form-input:focus {
  border-color: #e50914;
}

.form-select option {
  background: #141a20;
}

/* ===== 弹窗底部按钮 ===== */
.modal-footer {
  display: flex;
  gap: 12px;
  padding: 0 24px 24px;
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 50px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-cancel {
  background: rgba(255, 255, 255, 0.06);
  color: #8a949f;
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #e8edf2;
}

.btn-confirm {
  background: linear-gradient(135deg, #e50914, #b20710);
  color: #fff;
}

.btn-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(229, 9, 20, 0.3);
}

/* ===== 二维码弹窗 ===== */
.qr-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px 24px 24px;
}

.qr-canvas {
  width: 200px;
  height: 200px;
  border-radius: 12px;
  background: #0f1419;
  border: 1px solid #1a1f26;
}

.qr-info {
  text-align: center;
  width: 100%;
}

.qr-currency {
  color: #e8edf2;
  font-size: 16px;
  font-weight: 600;
}

.qr-address {
  color: #8a949f;
  font-size: 13px;
  font-family: monospace;
  word-break: break-all;
  margin: 4px 0 12px;
}

.copy-qr-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid #1a1f26;
  border-radius: 20px;
  color: #8a949f;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.25s;
}

.copy-qr-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #e8edf2;
}

/* ===== 详情弹窗 ===== */
.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-label {
  color: #5a6672;
  font-size: 14px;
}

.detail-value {
  color: #e8edf2;
  font-size: 14px;
  font-weight: 500;
}

.detail-value.address-full {
  color: #8a949f;
  font-family: monospace;
  font-size: 13px;
  word-break: break-all;
  max-width: 220px;
  text-align: right;
}

/* ============================================================
   动画
   ============================================================ */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ============================================================
   响应式
   ============================================================ */
@media (max-width: 820px) {
  .table-header {
    display: none;
  }

  .table-row {
    flex-direction: column;
    align-items: stretch;
    padding: 14px 16px;
    gap: 6px;
  }

  .row-cell {
    padding: 4px 0;
    min-height: auto;
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  }

  .row-cell::before {
    content: attr(data-label);
    color: #5a6672;
    font-size: 13px;
    font-weight: 500;
  }

  .row-cell:last-child {
    border-bottom: none;
    justify-content: center;
    padding-top: 6px;
  }

  .row-cell.currency::before { content: '币种'; }
  .row-cell.address::before { content: '地址'; }
  .row-cell.time::before { content: '时间'; }
  .row-cell.actions::before { content: ''; }

  .row-cell.currency { flex: none; }
  .row-cell.address { flex: none; }
  .row-cell.time { flex: none; }
  .row-cell.actions { flex: none; }

  .address-display {
    flex-wrap: wrap;
  }

  .address-text {
    font-size: 13px;
  }

  .modal-content {
    width: 95vw;
  }

  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .detail-value.address-full {
    max-width: 100%;
    text-align: left;
  }

  .qr-canvas {
    width: 160px;
    height: 160px;
  }
}

@media (max-width: 480px) {
  .filter-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .add-btn {
    justify-content: center;
    padding: 10px 16px;
  }

  .search-box {
    max-width: 100%;
  }

  .table-row {
    padding: 12px 14px;
    gap: 4px;
  }

  .row-cell {
    font-size: 13px;
    padding: 3px 0;
  }

  .row-cell::before {
    font-size: 12px;
  }

  .currency-name {
    font-size: 13px;
  }

  .address-text {
    font-size: 12px;
  }

  .time-date {
    font-size: 12px;
  }

  .time-diff {
    font-size: 11px;
  }

  .page-btn {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }

  .page-info {
    font-size: 13px;
  }

  .modal-content {
    padding: 0;
  }

  .modal-body {
    padding: 16px 18px 18px;
  }

  .modal-footer {
    padding: 0 18px 18px;
    flex-direction: column;
    gap: 8px;
  }

  .btn-cancel,
  .btn-confirm {
    padding: 10px;
    font-size: 14px;
  }

  .qr-canvas {
    width: 140px;
    height: 140px;
  }
}
</style>